import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  BarChart3, 
  FileText, 
  Users, 
  Eye, 
  TrendingUp, 
  Trash2, 
  Edit, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Zap,
  Facebook
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, recent, popular

  useEffect(() => {
    // Check if user is admin (you can add more sophisticated check)
    if (!user) {
      navigate('/login');
      return;
    }
    
    loadDashboardData();
  }, [user, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load stats
      const statsResponse = await axios.get(`${API_BASE_URL}/api/admin/stats`);
      setStats(statsResponse.data);
      
      // Load articles
      const articlesResponse = await axios.get(`${API_BASE_URL}/api/articles/?limit=100`);
      setArticles(articlesResponse.data);
      
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArticle = async (articleId, articleTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${articleTitle}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE_URL}/api/articles/${articleId}`);
      alert('Article deleted successfully');
      loadDashboardData(); // Reload
    } catch (error) {
      alert('Failed to delete article: ' + error.message);
    }
  };

  const handleEditArticle = (articleId) => {
    navigate(`/admin/edit-article/${articleId}`);
  };

  const getFilteredArticles = () => {
    let filtered = [...articles];
    
    if (filter === 'recent') {
      // Already sorted by date
      return filtered.slice(0, 20);
    } else if (filter === 'popular') {
      // Sort by view_count
      filtered.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
      return filtered.slice(0, 20);
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-600">Loading admin dashboard...</p>
      </div>
    );
  }

  const filteredArticles = getFilteredArticles();

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-serif font-bold mb-2 flex items-center gap-3">
            <BarChart3 size={32} className="text-blue-600" />
            Admin Dashboard
          </h1>
          <p className="text-gray-600">Monitor and manage The Central Coast Times</p>
        </div>
        <Button
          onClick={() => navigate('/admin/create-article')}
          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-6 text-lg"
        >
          <Zap size={20} className="mr-2" />
          Create Article with AI
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <FileText size={18} />
              Total Articles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.total_articles || articles.length}</div>
            <p className="text-xs text-gray-500 mt-1">Published articles</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Eye size={18} />
              Total Views
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {articles.reduce((sum, a) => sum + (a.view_count || 0), 0).toLocaleString()}
            </div>
            <p className="text-xs text-gray-500 mt-1">Article views</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Users size={18} />
              Categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {[...new Set(articles.map(a => a.category))].length}
            </div>
            <p className="text-xs text-gray-500 mt-1">Active categories</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <TrendingUp size={18} />
              Avg Views
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {Math.round(articles.reduce((sum, a) => sum + (a.view_count || 0), 0) / articles.length) || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Per article</p>
          </CardContent>
        </Card>
      </div>

      {/* Most Popular Articles */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="text-green-600" />
            Most Popular Articles
          </CardTitle>
          <CardDescription>Top 5 articles by view count</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {articles
              .sort((a, b) => (b.view_count || 0) - (a.view_count || 0))
              .slice(0, 5)
              .map((article, index) => (
                <div key={article.id || article._id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-400 w-8">#{index + 1}</div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm">{article.title}</h4>
                    <p className="text-xs text-gray-500">
                      {article.category} • {new Date(article.published_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">{article.view_count || 0}</div>
                    <div className="text-xs text-gray-500">views</div>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Article Management */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="text-blue-600" />
                Article Management
              </CardTitle>
              <CardDescription>View, edit, or delete articles</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                All
              </Button>
              <Button
                variant={filter === 'recent' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('recent')}
              >
                Recent
              </Button>
              <Button
                variant={filter === 'popular' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('popular')}
              >
                Popular
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredArticles.map((article) => (
              <div 
                key={article.id || article._id} 
                className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                {article.image && (
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-20 h-20 object-cover rounded"
                  />
                )}
                <div className="flex-1">
                  <h4 className="font-semibold text-sm mb-1">{article.title}</h4>
                  <div className="flex gap-3 text-xs text-gray-500">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {article.category}
                    </span>
                    <span>{article.author}</span>
                    <span>{new Date(article.published_date).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1">
                      <Eye size={12} /> {article.view_count || 0}
                    </span>
                    {article.automated && (
                      <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded flex items-center gap-1">
                        <Zap size={12} /> Auto
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const articleUrl = `${window.location.origin}/article/${article.slug}`;
                      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`;
                      window.open(facebookUrl, '_blank', 'width=600,height=400');
                    }}
                    className="bg-blue-50 hover:bg-blue-100"
                  >
                    <Facebook size={14} className="mr-1" />
                    Share
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/article/${article.slug}`, '_blank')}
                  >
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditArticle(article.id || article._id)}
                  >
                    <Edit size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteArticle(article.id || article._id, article.title)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          {filteredArticles.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No articles found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Facebook Auto-Posting Configuration */}
      <Card className="mt-8 border-blue-200 bg-blue-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Facebook className="text-blue-600" size={24} />
            Facebook Auto-Posting
          </CardTitle>
          <CardDescription>Configure automatic posting to Facebook when articles are published</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold mb-3">Setup Instructions</h4>
              <ol className="text-sm space-y-2 list-decimal list-inside text-gray-700">
                <li>Go to <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Facebook Graph API Explorer</a></li>
                <li>Select your Facebook Page</li>
                <li>Generate an access token with <code className="bg-gray-100 px-1">pages_manage_posts</code> permission</li>
                <li>Get your Page ID from your Facebook Page → About</li>
                <li>Enter both below and click "Save Configuration"</li>
              </ol>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Facebook Page ID</label>
                <input
                  type="text"
                  placeholder="Enter your Page ID"
                  className="w-full px-3 py-2 border rounded-lg"
                  id="facebook-page-id"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Access Token</label>
                <input
                  type="password"
                  placeholder="Enter your access token"
                  className="w-full px-3 py-2 border rounded-lg"
                  id="facebook-access-token"
                />
              </div>
              
              <Button
                onClick={async () => {
                  const pageId = document.getElementById('facebook-page-id').value;
                  const accessToken = document.getElementById('facebook-access-token').value;
                  
                  if (!pageId || !accessToken) {
                    alert('Please enter both Page ID and Access Token');
                    return;
                  }
                  
                  try {
                    const response = await axios.post(
                      `${API_BASE_URL}/api/admin/facebook/configure?page_id=${encodeURIComponent(pageId)}&access_token=${encodeURIComponent(accessToken)}`
                    );
                    
                    if (response.data.success) {
                      alert('Facebook configured successfully! ✓\n\nConnection test: ' + (response.data.test_result.success ? 'Passed' : 'Failed'));
                    }
                  } catch (error) {
                    alert('Configuration failed: ' + error.message);
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Save Configuration
              </Button>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3 text-sm text-gray-700">
              <p className="font-medium mb-1">What gets posted:</p>
              <ul className="space-y-1 text-xs">
                <li>✓ Article title and excerpt</li>
                <li>✓ Link back to your website</li>
                <li>✓ Main article image (if available)</li>
                <li>✓ Posts automatically when you publish an article</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
